import { ExportAllListActions } from '../ExportAllButton';
import React, { useEffect } from 'react';
import {
  ListGuesser,
  ShowGuesser,
  CreateGuesser,
  FieldGuesser,
  InputGuesser,
} from '@api-platform/admin';
import {
  Edit,
  NumberField,
  TextField,
  ReferenceField,
  ReferenceInput,
  SimpleForm,
  DateField,
  AutocompleteInput,
  FormDataConsumer,
  TextInput,
  DateInput,
  FunctionField,
  SelectInput,
  useRecordContext,
  TopToolbar,
  EditButton,
  ShowButton,
  useRedirect,
  useGetIdentity,
  useGetOne,
  useNotify,
} from 'react-admin';
import { CourseCityDateInputs } from './Inputs';
import { CourseTypesList } from '../../constants/Course';
import { Button } from '@mui/material';
import RateReviewIcon from '@mui/icons-material/RateReview';
import { Link } from 'react-router-dom';
import { isGranted } from '../../helper/security';
import { ROLES } from '../../constants/Roles';
import { getSelectedCompanyFilter } from '../../helper/filter';
import { SupplierFilter } from '../Inputs/SupplierInput';
import { courseCityDateExporter } from './exporter';

export const CourseCityDateList = (props) => {
  const selectedCompanyFilter = getSelectedCompanyFilter();
  const filters = [
    <TextInput label="Course" source="courseCity.course.code" />,
    <TextInput label="City" source="courseCity.city.name" />,
    <DateInput label="Start Date" source="startDate[after]" />,
    <DateInput label="End Date" source="endDate[after]" />,
    <DateInput label="Show Until" source="showUntil[after]" />,
    <SelectInput
      label={'Course Type'}
      source="courseCity.course.type"
      choices={CourseTypesList}
    />,
    <SupplierFilter
      source="supplier"
      company={selectedCompanyFilter?.company}
    />,
  ];

  const companyFilter = getSelectedCompanyFilter('courseCity.course');

  return (
    <ListGuesser
      {...props}
      actions={<ExportAllListActions />}
      filters={filters}
      filter={companyFilter}
      exporter={courseCityDateExporter}
    >
      <ReferenceField
        label="Course"
        source="course"
        sortBy={'courseCity.course.code'}
        reference="courses"
        link="show"
      >
        <TextField source="code" />
      </ReferenceField>
      <ReferenceField
        label="City"
        source="courseCity"
        sortBy={'courseCity.city.name'}
        reference="course_cities"
        link="show"
      >
        <TextField source="cityView" />
      </ReferenceField>
      <TextField source="supplier" label="Supplier" />
      <DateField source={'startDate'} locales={'de'} />
      <DateField source={'endDate'} locales={'de'} />
      <DateField source={'showUntil'} locales={'de'} />
      <ReferenceField
        label="Course Type"
        source="course"
        sortBy={'courseCity.course.type'}
        reference="courses"
        link={false}
      >
        <FunctionField
          source={'type'}
          render={(record) =>
            CourseTypesList.filter((item) => item.id === record.type)[0].name
          }
        />
      </ReferenceField>
      <NumberField source={'drupalId'} locales={'es'} />
      <ReferenceField
        label="CreatedBy"
        source="createdBy"
        sortBy={'createdBy.email'}
        reference="users"
      >
        <TextField source="email" />
      </ReferenceField>
      <DateField source={'updatedAt'} locales={'de'} showTime />
      <DateField source={'createdAt'} locales={'de'} showTime />
    </ListGuesser>
  );
};

export const CourseCityDateShow = (props) => (
  <ShowGuesser
    actions={<CourseCityDateActions isOnEditPage={false} />}
    {...props}
  >
    <TextField fullWidth source={'id'} />
    <NumberField fullWidth source={'drupalId'} locales={'es'} />
    <FieldGuesser fullWidth source={'startDate'} />
    <FieldGuesser fullWidth source={'endDate'} />
    <FieldGuesser fullWidth source={'showUntil'} />
    <FieldGuesser fullWidth source={'createdAt'} />
    <FieldGuesser fullWidth source={'updatedAt'} />
    <ReferenceField
      fullWidth
      label="CreatedBy"
      source="createdBy"
      reference="users"
    >
      <TextField source="email" />
    </ReferenceField>
    <ReferenceField
      fullWidth
      label="UpdatedBy"
      source="updatedBy"
      reference="users"
    >
      <TextField source="email" />
    </ReferenceField>
  </ShowGuesser>
);

export const CourseCityDateCreate = (props) => (
  <CreateGuesser {...props}>
    <InputGuesser source={'course'} />
    <ReferenceInput
      source="city"
      reference="cities"
      label="City"
      filterToQuery={(searchText) => ({ name: searchText })}
    >
      <AutocompleteInput
        optionValue="@id"
        optionText={(record) => record.name}
      />
    </ReferenceInput>
  </CreateGuesser>
);

export const CourseCityDateEdit = (props) => {
  const redirect = useRedirect();
  const notify = useNotify();

  const transform = (formData) => {
    let taxPercentage = formData.taxPercentage;
    let price = formData.price;
    price = price && typeof price !== 'string' ? price.toString() : price;
    taxPercentage =
      taxPercentage && typeof taxPercentage !== 'string'
        ? taxPercentage.toString()
        : taxPercentage;

    let additionalCosts = formData.additionalCosts;
    additionalCosts =
      additionalCosts && typeof additionalCosts !== 'string'
        ? additionalCosts.toString()
        : additionalCosts;

    if (typeof price === 'string' && price.includes(',')) {
      price = price.replace(',', '.');
    }

    if (typeof taxPercentage === 'string' && taxPercentage.includes(',')) {
      taxPercentage = taxPercentage.replace(',', '.');
    }

    if (
      typeof additionalCosts === 'string' &&
      additionalCosts.includes(',')
    ) {
      additionalCosts = additionalCosts.replace(',', '.');
    }

    return {
      ...formData,
      price,
      taxPercentage,
      additionalCosts,
    };
  };

  const onSuccess = (updatedDate) => {
    const courseId =
      typeof updatedDate.course === 'object'
        ? updatedDate.course['@id'] || updatedDate.course.id
        : updatedDate.course;

    notify('Course city date updated', { type: 'success' });

    if (courseId) {
      redirect(`/courses/${encodeURIComponent(courseId)}/calendar`);
    }
  };

  return (
    <Edit
      {...props}
      actions={<CourseCityDateActions isOnEditPage={true} />}
      transform={transform}
      mutationMode="pessimistic"
      mutationOptions={{ onSuccess }}
    >
      <CourseCityDateEditForm />
    </Edit>
  );
};

export const CourseCityDateEditForm = (props) => {
  const { data } = useGetIdentity();
  const record = useRecordContext();
  const redirect = useRedirect();
  const courseId =
    typeof record?.course === 'object'
      ? record.course['@id'] || record.course.id
      : record?.course;
  const {
    data: course,
    isPending: isCoursePending,
  } = useGetOne(
    'courses',
    { id: courseId },
    { enabled: Boolean(courseId) }
  );

  useEffect(() => {
    if (!record || isCoursePending || !data) {
      return;
    }

    const canCourseCreatorEdit =
      data.roles?.includes(ROLES.ROLE_COURSE_CREATOR) &&
      (
        course?.editableByCourseCreator ||
        (
          course?.createdByEmail === data.username &&
          !course?.enabled &&
          record.drupalId === null
        )
      );

    if (
      !isGranted(ROLES.ROLE_BRANCH_ADMIN, data) &&
      !canCourseCreatorEdit
    ) {
      redirect('show', 'course_city_dates', record.id);
    }
  }, [record, data, course, isCoursePending, redirect]);

  if (!record) {
    return null;
  }

  return (
    <SimpleForm
      sanitizeEmptyValues={true}
      // defaultValues={{course: data.id}}
    >
      <FormDataConsumer>
        {({ getSource, formData }) => {
          return (
            <CourseCityDateInputs
              scopedFormData={formData}
              company={course?.company}
              companySupplier={course?.supplier}
            />
          );
        }}
      </FormDataConsumer>
    </SimpleForm>
  );
};

const CourseCityDateActions = ({ isOnEditPage }) => {
  const record = useRecordContext();

  if (!record) {
    return null;
  }

  return (
    <TopToolbar>
      {isOnEditPage ? <ShowButton /> : <EditButton />}
      <Button
        startIcon={<RateReviewIcon />}
        component={Link}
        color="primary"
        size={'small'}
        to={`/course_city_dates/${encodeURIComponent(record.id)}/review-codes`}
        sx={{ lineHeight: '1.5' }}
      >
        Review Codes
      </Button>
    </TopToolbar>
  );
};
